import React, { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';

/**
 * Safe CountUp wrapper that prevents innerHTML null errors
 * @param {object} props - CountUp props
 * @returns {JSX.Element} Safe CountUp component
 */
const SafeCountUp = ({ 
  start = 0, 
  end = 0, 
  duration = 3, 
  delay = 0,
  onComplete = null,
  ...otherProps 
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const countUpRef = useRef(null);

  useEffect(() => {
    // Set mounted flag
    setIsMounted(true);

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, delay || 100);

    return () => {
      setIsMounted(false);
      clearTimeout(timer);
    };
  }, [delay]);

  // If not mounted or shouldn't animate yet, show the end value
  if (!isMounted || !shouldAnimate) {
    const formattedValue = formatValue(end, otherProps);
    return <span ref={countUpRef}>{formattedValue}</span>;
  }

  return (
    <CountUp
      start={start}
      end={end}
      duration={duration}
      onComplete={onComplete}
      preserveValue={true}
      {...otherProps}
    />
  );
};

/**
 * Format value according to CountUp props
 */
const formatValue = (value, props) => {
  let formatted = value;

  // Handle decimals
  if (props.decimals && props.decimals > 0) {
    formatted = Number(value).toFixed(props.decimals);
  }

  // Handle prefix
  if (props.prefix) {
    formatted = props.prefix + formatted;
  }

  // Handle suffix
  if (props.suffix) {
    formatted = formatted + props.suffix;
  }

  // Handle separator (basic implementation)
  if (props.separator && !props.prefix && !props.suffix) {
    formatted = Number(value).toLocaleString();
  }

  return formatted;
};

export default SafeCountUp; 